import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  MedicalServices as MedicalIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  FamilyRestroom as FamilyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchTodaysPendingAppointments,
  fetchTodaysAcceptedAppointments,
  fetchInProgressAppointments,
  acceptAppointment,
  startVisit,
  arriveAtLocation,
  completeExamination,
} from '../../store/slices/appointmentSlice';
import { fetchPatientProfile } from '../../store/slices/patientProfileSlice';
import PatientProfileDialog from '../../components/PatientProfileDialog';
import StartVisitDialog from '../../components/StartVisitDialog';
import MedicalExaminationForm from '../../components/MedicalExaminationForm';
import { PatientProfile } from '../../types/patient';

interface Appointment {
  id: number;
  bookingNumber: string;
  officerId: number;
  officerName: string;
  officerMobile: string;
  officerDesignation: string;
  officerDepartment: string;
  officerAddress: string;
  familyMemberId: number | null;
  familyMemberName: string | null;
  familyMemberRelationship: string | null;
  patientName: string;
  patientMobile: string;
  patientAge: number | null;
  patientGender: string | null;
  patientBloodGroup?: string | null;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  appointmentTypeDisplayName?: string;
  status: string;
  statusDisplayName?: string;
  priority?: string;
  priorityDisplayName?: string;
  reasonForVisit?: string;
  symptoms?: string;
  chiefComplaints?: string;
  notes?: string;
  specialNotes?: string;
  fullVisitAddress?: string;
  visitAddressLine1?: string;
  visitAddressLine2?: string;
  visitCity?: string;
  visitState?: string;
  visitPincode?: string;
  visitLandmark?: string;
  consultationFee?: number;
  visitFee?: number;
  createdAt?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/**
 * Doctor Appointment Center Page
 * Premium appointment management for Government Doctors with complete details.
 *
 * @author Civilhealth Development Team
 */
const AppointmentCenterPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { appointments, loading, workflowLoading, workflowError } = useSelector((state: RootState) => state.appointment);
  const { profile: patientProfile, loading: profileLoading } = useSelector((state: RootState) => state.patientProfile);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [startVisitDialogOpen, setStartVisitDialogOpen] = useState(false);
  const [examinationFormOpen, setExaminationFormOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Redirect if not authenticated or not a doctor
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (user && user.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/', { replace: true });
      return;
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch appointments based on active tab
  useEffect(() => {
    if (isAuthenticated && user?.role === 'GOVERNMENT_DOCTOR') {
      loadAppointments();
    }
  }, [dispatch, isAuthenticated, user, activeTab]);

  const loadAppointments = () => {
    setRefreshing(true);
    switch (activeTab) {
      case 0: // Pending
        dispatch(fetchTodaysPendingAppointments());
        break;
      case 1: // Accepted
        dispatch(fetchTodaysAcceptedAppointments());
        break;
      case 2: // In Progress
        dispatch(fetchInProgressAppointments());
        break;
      default:
        dispatch(fetchTodaysPendingAppointments());
    }
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedAppointment(null);
  };

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setDetailsDialogOpen(true);
  };

  const handleViewPatientProfile = (appointmentId: number) => {
    dispatch(fetchPatientProfile(appointmentId));
    setProfileDialogOpen(true);
  };

  const handleAcceptAppointment = async (appointmentId: number) => {
    try {
      await dispatch(acceptAppointment(appointmentId)).unwrap();
      loadAppointments();
    } catch (error) {
      console.error('Failed to accept appointment:', error);
    }
  };

  const handleStartVisit = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setStartVisitDialogOpen(true);
  };

  const confirmStartVisit = async () => {
    if (!selectedAppointment) return;
    try {
      await dispatch(startVisit({ appointmentId: selectedAppointment.id })).unwrap();
      setStartVisitDialogOpen(false);
      setActiveTab(2); // Switch to In Progress
      loadAppointments();
    } catch (error) {
      console.error('Failed to start visit:', error);
    }
  };

  const handleArriveAtLocation = async (appointmentId: number) => {
    try {
      await dispatch(arriveAtLocation({ appointmentId })).unwrap();
      loadAppointments();
    } catch (error) {
      console.error('Failed to mark arrival:', error);
    }
  };

  const handleCompleteExamination = async (appointmentId: number, visitNotes: string) => {
    try {
      await dispatch(completeExamination({ appointmentId, notes: visitNotes })).unwrap();
      loadAppointments();
      setNotesDialogOpen(false);
    } catch (error) {
      console.error('Failed to complete examination:', error);
    }
  };

  // Get status color - New Flow: PENDING -> ACCEPTED -> IN_PROGRESS -> VISITED -> CLOSED
  const getStatusColor = (status: string): 'warning' | 'info' | 'primary' | 'success' | 'error' | 'default' => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'warning';
      case 'ACCEPTED':
        return 'info';
      case 'IN_PROGRESS':
        return 'primary';
      case 'VISITED':
        return 'info';
      case 'CLOSED':
        return 'success';
      case 'CANCELLED':
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status label - New Flow: PENDING -> ACCEPTED -> IN_PROGRESS -> VISITED -> CLOSED
  const getStatusLabel = (status: string): string => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'Pending';
      case 'ACCEPTED':
        return 'Accepted';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'VISITED':
        return 'Visited';
      case 'CLOSED':
        return 'Closed';
      case 'CANCELLED':
        return 'Cancelled';
      case 'REJECTED':
        return 'Rejected';
      case 'NO_SHOW':
        return 'No Show';
      case 'RESCHEDULED':
        return 'Rescheduled';
      default:
        return status || 'Unknown';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority?.toUpperCase()) {
      case 'EMERGENCY':
      case 'URGENT':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'NORMAL':
        return 'info';
      case 'LOW':
        return 'default';
      default:
        return 'default';
    }
  };

  // Appointment Card Component
  const AppointmentCard: React.FC<{ appointment: Appointment; showActions?: boolean }> = ({ appointment, showActions = true }) => {
    const isFamilyMember = appointment.familyMemberId !== null;
    const status = appointment.status?.toUpperCase();

    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: status === 'PENDING' ? '2px solid #f9ab00' : status === 'IN_PROGRESS' ? '2px solid #1a73e8' : 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
        }}
      >
        <CardContent>
          {/* Header: Patient Name + Status + Priority */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* HIGH CONTRAST: Light background + Dark icon */}
              <Avatar sx={{ bgcolor: isFamilyMember ? '#EFF6FF' : '#DCFCE7', color: isFamilyMember ? '#1D4ED8' : '#15803D', width: 48, height: 48, border: '2px solid rgba(0,0,0,0.1)' }}>
                {isFamilyMember ? <FamilyIcon /> : <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {appointment.patientName || 'Patient'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={getStatusLabel(appointment.status)}
                    size="small"
                    color={getStatusColor(appointment.status)}
                  />
                  {appointment.priority && appointment.priority !== 'NORMAL' && (
                    <Chip
                      label={appointment.priorityDisplayName || appointment.priority}
                      size="small"
                      color={getPriorityColor(appointment.priority)}
                      variant="outlined"
                    />
                  )}
                  {isFamilyMember && (
                    <Chip
                      icon={<FamilyIcon />}
                      label={appointment.familyMemberRelationship || 'Family'}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                #{appointment.bookingNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {appointment.appointmentDate} | {appointment.appointmentTime}
              </Typography>
            </Box>
          </Box>

          {/* Officer Information */}
          <Paper sx={{ p: 2, bgcolor: 'transparent' , borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon fontSize="small" />
              Officer Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {appointment.officerName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {appointment.officerDesignation || 'Officer'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <BusinessIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {appointment.officerDepartment || 'Government Department'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" />
                  {appointment.officerMobile || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Visit Details */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {/* HIGH CONTRAST: Light background for better readability */}
              <Paper sx={{ p: 1.5, bgcolor: 'transparent' , borderRadius: 2, border: '1px solid rgba(29, 78, 216, 0.2)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#1D4ED8' }}>
                  <LocationIcon fontSize="small" sx={{ color: '#1D4ED8' }} />
                  Visit Address
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#374151' }}>
                  {appointment.fullVisitAddress || 'Address not provided'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* HIGH CONTRAST: Light amber background + dark text */}
              <Paper sx={{ p: 1.5, bgcolor: 'transparent' , borderRadius: 2, border: '1px solid rgba(180, 83, 9, 0.2)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#B45309' }}>
                  <MedicalIcon fontSize="small" sx={{ color: '#B45309' }} />
                  Reason for Visit
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {appointment.reasonForVisit || appointment.chiefComplaints || 'General Consultation'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Special Notes */}
          {appointment.specialNotes && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2">Special Notes:</Typography>
              <Typography variant="body2">{appointment.specialNotes}</Typography>
            </Alert>
          )}

          {/* Actions - Based on Status */}
          {showActions && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* PENDING: Accept button only */}
              {status === 'PENDING' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleAcceptAppointment(appointment.id)}
                    disabled={workflowLoading}
                  >
                    Accept Appointment
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(appointment)}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<PersonIcon />}
                    onClick={() => handleViewPatientProfile(appointment.id)}
                  >
                    Patient Profile
                  </Button>
                </>
              )}

              {/* ACCEPTED: Start Visit + Open Workflow */}
              {status === 'ACCEPTED' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<PlayIcon />}
                    onClick={() => handleStartVisit(appointment)}
                    disabled={workflowLoading}
                  >
                    Start Visit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<CalendarIcon />}
                    onClick={() => navigate(`/doctor/visit/${appointment.id}`)}
                  >
                    Open Workflow
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(appointment)}
                  >
                    Details
                  </Button>
                </>
              )}

              {/* IN_PROGRESS: Mark Arrived */}
              {status === 'IN_PROGRESS' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="info"
                    startIcon={<LocationIcon />}
                    onClick={() => handleArriveAtLocation(appointment.id)}
                    disabled={workflowLoading}
                  >
                    Mark Arrived
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MedicalIcon />}
                    onClick={() => navigate(`/doctor/visit/${appointment.id}`)}
                  >
                    Open Workflow
                  </Button>
                </>
              )}

              {/* VISITED: Create Visit Report */}
              {status === 'VISITED' && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<MedicalIcon />}
                    onClick={() => navigate(`/doctor/visit/${appointment.id}`)}
                  >
                    Create Visit Report
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(appointment)}
                  >
                    Details
                  </Button>
                </>
              )}

              {/* CLOSED: View Report */}
              {status === 'CLOSED' && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(appointment)}
                  >
                    View Report
                  </Button>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading && !appointments.length) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading appointments...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto', bgcolor: 'transparent' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
            Appointment Center
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your appointments with complete patient and officer details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAppointments}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => navigate('/doctor/schedule')}
          >
            My Schedule
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {workflowError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {workflowError}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab
            icon={<ScheduleIcon />}
            label={
              <Badge badgeContent={activeTab === 0 ? appointments.length : 0} color="warning">
                Pending
              </Badge>
            }
            iconPosition="start"
          />
          <Tab
            icon={<CheckIcon />}
            label={
              <Badge badgeContent={activeTab === 1 ? appointments.length : 0} color="info">
                Accepted
              </Badge>
            }
            iconPosition="start"
          />
          <Tab
            icon={<PlayIcon />}
            label={
              <Badge badgeContent={activeTab === 2 ? appointments.length : 0} color="primary">
                In Progress
              </Badge>
            }
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Pending Tab */}
        {appointments.length > 0 ? (
          appointments.map((appointment: Appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ScheduleIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Pending Appointments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All appointments have been processed or no new appointments scheduled
            </Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Accepted Tab */}
        {appointments.length > 0 ? (
          appointments.map((appointment: Appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Accepted Appointments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Accept pending appointments to see them here
            </Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* In Progress Tab */}
        {appointments.length > 0 ? (
          appointments.map((appointment: Appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <PlayIcon sx={{ fontSize: 64, color: '#dadce0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No In-Progress Visits
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start visits to see them here
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Patient Profile Dialog */}
      <PatientProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        profile={patientProfile as PatientProfile | null}
        loading={profileLoading}
      />

      {/* Start Visit Dialog */}
      <StartVisitDialog
        open={startVisitDialogOpen}
        onClose={() => setStartVisitDialogOpen(false)}
        onConfirm={confirmStartVisit}
        appointment={selectedAppointment}
        loading={workflowLoading}
      />

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Appointment Details - {selectedAppointment?.bookingNumber}
            </Typography>
            <Chip
              label={getStatusLabel(selectedAppointment?.status || '')}
              color={getStatusColor(selectedAppointment?.status || '')}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAppointment && (
            <Box>
              {/* Patient Information */}
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  Patient Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Patient Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.patientName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Contact Number</Typography>
                    <Typography variant="body1">{selectedAppointment.patientMobile || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Age / Gender</Typography>
                    <Typography variant="body1">{selectedAppointment.patientAge || 'N/A'} / {selectedAppointment.patientGender || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Relationship</Typography>
                    <Typography variant="body1">
                      {selectedAppointment.familyMemberId ? (
                        <Chip size="small" label={selectedAppointment.familyMemberRelationship || 'Family'} color="primary" variant="outlined" />
                      ) : 'Self'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Officer Information */}
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BadgeIcon />
                  Officer Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Officer Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedAppointment.officerName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Designation</Typography>
                    <Typography variant="body1">{selectedAppointment.officerDesignation || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Department</Typography>
                    <Typography variant="body1">{selectedAppointment.officerDepartment || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Contact Number</Typography>
                    <Typography variant="body1">{selectedAppointment.officerMobile || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography variant="body1">{selectedAppointment.officerAddress || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Visit Details */}
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon />
                  Visit Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Visit Address</Typography>
                    <Typography variant="body1">{selectedAppointment.fullVisitAddress || 'Address not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Appointment Date</Typography>
                    <Typography variant="body1">{selectedAppointment.appointmentDate}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Appointment Time</Typography>
                    <Typography variant="body1">{selectedAppointment.appointmentTime}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Appointment Type</Typography>
                    <Typography variant="body1">{selectedAppointment.appointmentType?.replace('_', ' ') || 'Home Visit'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Priority</Typography>
                    <Chip
                      size="small"
                      label={selectedAppointment.priorityDisplayName || selectedAppointment.priority || 'Normal'}
                      color={getPriorityColor(selectedAppointment.priority || 'Normal')}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Medical Information */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalIcon />
                  Medical Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Reason for Visit</Typography>
                    <Typography variant="body1">{selectedAppointment.reasonForVisit || 'General Consultation'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Chief Complaints</Typography>
                    <Typography variant="body1">{selectedAppointment.chiefComplaints || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Symptoms</Typography>
                    <Typography variant="body1">{selectedAppointment.symptoms || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Special Notes */}
              {selectedAppointment.specialNotes && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2">Special Notes:</Typography>
                  <Typography variant="body2">{selectedAppointment.specialNotes}</Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PersonIcon />}
            onClick={() => {
              setDetailsDialogOpen(false);
              if (selectedAppointment) {
                handleViewPatientProfile(selectedAppointment.id);
              }
            }}
          >
            View Patient Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Examination Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Complete Examination</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add notes about the examination before completing.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Examination Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter your notes about the patient's condition, diagnosis, and treatment..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleCompleteExamination(selectedAppointment?.id || 0, notes)}
            disabled={workflowLoading}
          >
            Complete Examination
          </Button>
        </DialogActions>
      </Dialog>

      {/* Medical Examination Form */}
      <MedicalExaminationForm
        open={examinationFormOpen}
        onClose={() => setExaminationFormOpen(false)}
        appointmentId={selectedAppointment?.id || 0}
        appointmentData={selectedAppointment ? {
          patientName: selectedAppointment.patientName,
          patientAge: selectedAppointment.patientAge,
          patientGender: selectedAppointment.patientGender,
          bookingNumber: selectedAppointment.bookingNumber,
          chiefComplaints: selectedAppointment.chiefComplaints,
          reasonForVisit: selectedAppointment.reasonForVisit,
        } : undefined}
        onSubmitSuccess={() => {
          setExaminationFormOpen(false);
          loadAppointments();
        }}
      />
    </Box>
  );
};

export default AppointmentCenterPage;